import olMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Circle from 'ol/style/Circle';
import { MapBrowserEvent } from 'ol';
import { Modify, Draw, Translate } from 'ol/interaction';
import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';
import GeometryType from 'ol/geom/GeometryType';
import { CommandStack, MultiGeometryCommand, GeometryCommand } from './GpxEditCommands';
import { createEditLineStyle } from './GpxEditStyles';
import { GpxWaypointLayer } from './GpxWaypointLayer';
import { GpxLayerZIndex } from './GpxLayerOrder';
import { GpxEditSubMode } from './GpxEditToolbar';

export interface IGpxEditInteractionsHost {
    /** Open the waypoint dialog to create a waypoint at the clicked position. */
    onWaypointAddRequested(lonLat: [number, number]): void;
    /** Open the waypoint dialog to edit an existing draft. */
    onWaypointEditRequested(feature: Feature): void;
    /** A waypoint marker finished a Translate drag. */
    onWaypointMoved(feature: Feature, beforeLonLat: [number, number], afterLonLat: [number, number]): void;
    /** Geometry changed via undo/redo or a tool — refresh dependent UI (trim dots). */
    onGeometryChanged(): void;
    /** The interactions auto-transitioned sub-mode (e.g. extend finished) — the
     *  session performs the authoritative switch so the toolbar highlight stays in sync. */
    onSubModeChanged(mode: GpxEditSubMode): void;
    /** The extend sketch gained or lost a point — Undo's availability depends on it. */
    onSketchChanged(): void;
}

const TRIM_HIT_TOLERANCE_PX = 10;

/**
 * Wires the OpenLayers interactions for each edit sub-mode. Exactly one
 * sub-mode's interaction set is active at a time; switching tears down the
 * previous set. Commands are pushed onto the session's CommandStack.
 *
 * Future seam: attachSnap(source) can add an ol/interaction/Snap against a
 * byway WFS source alongside Modify/Draw.
 */
/** Trim-mode vertex: white, so it reads as a target rather than part of the line. */
const TRIM_DOT_STYLE = new Style({
    image: new Circle({
        radius: 5,
        fill: new Fill({ color: '#ffffff' }),
        stroke: new Stroke({ color: '#c9302c', width: 2 })
    })
});

/** The same vertex once a hovered trim would delete it: filled, not outlined. */
const TRIM_DOOMED_DOT_STYLE = new Style({
    image: new Circle({
        radius: 5,
        fill: new Fill({ color: '#c9302c' }),
        stroke: new Stroke({ color: '#ffffff', width: 1.5 })
    })
});

export class GpxEditInteractions {

    private mode: GpxEditSubMode | null = null;
    private modify: Modify | null = null;
    private draw: Draw | null = null;
    private translate: Translate | null = null;
    private clickListenerKey: any = null;
    private modifySnapshot: Array<{ feature: Feature; before: Coordinate[] }> = [];
    private translateSnapshot: [number, number] | null = null;
    private trimDotsLayer: VectorLayer | null = null;
    private trimPreviewLayer: VectorLayer | null = null;
    private pointerMoveListenerKey: any = null;
    private previewedTrim: { feature: Feature; index: number } | null = null;
    private extendFeature: Feature | null = null;
    private extendBefore: Coordinate[] | null = null;
    private sketchClickListenerKey: any = null;

    public constructor(
        private map: olMap,
        private workingSource: VectorSource,
        private commandStack: CommandStack,
        private waypointLayer: GpxWaypointLayer,
        private host: IGpxEditInteractionsHost,
        private getEditColour: () => string
    ) {
    }

    /** The Draw currently sketching (extend/new) — lets undo remove the last point. */
    get activeSketchDraw(): Draw | null {
        return this.draw;
    }

    /**
     * How many points the user has added during the running extend sketch.
     *
     * Undo has to stop at zero: Draw.extend() hands the whole existing line to the
     * sketch, so removeLastPoint() keeps going happily into the original route once
     * the new points are gone — silently shortening what the user only meant to
     * extend. There is no per-point event to count, so this reads the sketch
     * geometry, which Draw mutates in place as points land.
     */
    get extendPointsPlaced(): number {
        if (!this.draw || !this.extendFeature || !this.extendBefore) {
            return 0;
        }
        const current = (<LineString>this.extendFeature.getGeometry()).getCoordinates().length;
        return Math.max(0, current - this.extendBefore.length);
    }

    /**
     * Activates one tool, tearing down whatever was running — only ever one at a
     * time. null leaves the map inert, which is how a session starts and where a
     * one-shot action like Reverse puts it.
     */
    public setSubMode = (mode: GpxEditSubMode | null): void => {
        if (this.mode === mode) {
            return;
        }
        this.clearInteractions();
        this.mode = mode;

        switch (mode) {
            case 'geometry':
                this.startGeometry();
                break;
            case 'extend':
                this.startExtend();
                break;
            case 'trim':
                this.startTrim();
                break;
            case 'waypoints':
                this.startWaypoints();
                break;
        }
    }

    public clearInteractions = (): void => {
        if (this.modify) {
            this.map.removeInteraction(this.modify);
            this.modify = null;
        }
        if (this.draw) {
            //abort any in-progress sketch; the feature keeps its pre-extend shape
            if (this.extendFeature && this.extendBefore) {
                (<LineString>this.extendFeature.getGeometry()).setCoordinates(this.extendBefore);
                if (!this.workingSource.hasFeature(this.extendFeature)) {
                    this.workingSource.addFeature(this.extendFeature);
                }
            }
            this.map.removeInteraction(this.draw);
            this.draw = null;
            this.extendFeature = null;
            this.extendBefore = null;
        }
        if (this.sketchClickListenerKey) {
            this.map.un('click', this.sketchClickListenerKey);
            this.sketchClickListenerKey = null;
            this.host.onSketchChanged();
        }
        if (this.translate) {
            this.map.removeInteraction(this.translate);
            this.translate = null;
        }
        if (this.clickListenerKey) {
            this.map.un('singleclick', this.clickListenerKey);
            this.clickListenerKey = null;
        }
        if (this.pointerMoveListenerKey) {
            this.map.un('pointermove', this.pointerMoveListenerKey);
            this.pointerMoveListenerKey = null;
        }
        this.removeTrimDots();
        this.removeTrimPreview();
        this.setCursor('');
        this.mode = null;
    }

    /** Refreshes sub-mode visuals after undo/redo mutates geometry. */
    public refreshForGeometryChange = (): void => {
        if (this.mode === 'trim') {
            this.rebuildTrimDots();
            //the preview was computed against coordinates that have just moved
            this.removeTrimPreview();
        }
    }

    // -------------------------------------------------------------------------
    // geometry — drag vertices (Modify)
    // -------------------------------------------------------------------------

    private startGeometry(): void {
        const self = this;

        this.modify = new Modify({ source: this.workingSource });

        this.modify.on('modifystart', function () {
            self.modifySnapshot = self.workingSource.getFeatures().map(feature => ({
                feature: feature,
                before: self.cloneCoords(feature)
            }));
        });

        this.modify.on('modifyend', function () {
            const changes = self.modifySnapshot
                .map(snapshot => ({
                    feature: snapshot.feature,
                    before: snapshot.before,
                    after: self.cloneCoords(snapshot.feature)
                }))
                .filter(change => !self.coordsEqual(change.before, change.after));

            if (changes.length > 0) {
                self.commandStack.execute(
                    new MultiGeometryCommand('Modify route', changes, self.host.onGeometryChanged),
                    true);
            }
            self.modifySnapshot = [];
        });

        this.map.addInteraction(this.modify);
    }

    // -------------------------------------------------------------------------
    // extend — continue drawing from the end of the line (Draw.extend)
    // -------------------------------------------------------------------------

    private startExtend(): void {
        const self = this;

        //extend the last segment (single-segment routes: the whole line)
        const features = this.workingSource.getFeatures();
        const feature = features.length > 0 ? features[features.length - 1] : null;
        if (!feature) {
            return;
        }

        this.extendFeature = feature;
        this.extendBefore = this.cloneCoords(feature);

        //remove from the source while sketching so the line isn't drawn twice;
        //Draw re-adds it on finish
        this.workingSource.removeFeature(feature);

        this.draw = new Draw({
            source: this.workingSource,
            type: GeometryType.LINE_STRING,
            style: createEditLineStyle(this.getEditColour())
        });
        this.map.addInteraction(this.draw);
        this.draw.extend(feature as Feature<LineString>);
        this.setCursor('crosshair');

        //no per-point event exists, so re-read the sketch after each click
        this.sketchClickListenerKey = () => self.host.onSketchChanged();
        this.map.on('click', this.sketchClickListenerKey);

        this.draw.on('drawend', function () {
            const before = self.extendBefore;
            self.extendFeature = null;
            self.extendBefore = null;

            //defer so Draw finishes re-adding the feature to the source first
            window.setTimeout(() => {
                if (self.draw) {
                    self.map.removeInteraction(self.draw);
                    self.draw = null;
                }

                const after = self.cloneCoords(feature);
                if (before && !self.coordsEqual(before, after)) {
                    self.pushExtendCommands(feature, before, after);
                }

                //fall back to vertex-drag mode after finishing the extension — route
                //through the host so the session updates the toolbar highlight too
                self.mode = null;
                self.host.onSubModeChanged('geometry');
            }, 0);
        });
    }

    // -------------------------------------------------------------------------
    // trim — click a vertex to remove it and everything after it
    // -------------------------------------------------------------------------

    private startTrim(): void {
        const self = this;
        this.rebuildTrimDots();
        this.setCursor('pointer');

        this.clickListenerKey = (evt: MapBrowserEvent) => self.handleTrimClick(evt);
        this.map.on('singleclick', this.clickListenerKey);

        //show what a click would remove before it is removed
        this.pointerMoveListenerKey = (evt: MapBrowserEvent) => self.handleTrimHover(evt);
        this.map.on('pointermove', this.pointerMoveListenerKey);
    }

    /**
     * The vertex a trim click would act on: the nearest one within the hit
     * tolerance that would actually remove something. Shared by the click and the
     * hover preview so the two can never disagree about what a click will do —
     * highlighting one stretch and deleting another would be worse than no preview.
     */
    private findTrimTarget(evt: MapBrowserEvent): { feature: Feature; index: number } | null {
        let best: { feature: Feature; index: number; distance: number } | null = null;

        for (const feature of this.workingSource.getFeatures()) {
            const coords = (<LineString>feature.getGeometry()).getCoordinates();
            for (let i = 0; i < coords.length; i++) {
                const pixel = this.map.getPixelFromCoordinate(coords[i]);
                const dx = pixel[0] - evt.pixel[0];
                const dy = pixel[1] - evt.pixel[1];
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance <= TRIM_HIT_TOLERANCE_PX && (!best || distance < best.distance)) {
                    best = { feature: feature, index: i, distance: distance };
                }
            }
        }

        if (!best || best.index < 1) {
            //no vertex hit, or trimming here would leave fewer than 2 points
            return null;
        }
        const coords = (<LineString>best.feature.getGeometry()).getCoordinates();
        if (best.index >= coords.length - 1) {
            //the last vertex: nothing after it to remove
            return null;
        }
        return { feature: best.feature, index: best.index };
    }

    private handleTrimClick(evt: MapBrowserEvent): void {
        const target = this.findTrimTarget(evt);
        if (!target) {
            return;
        }

        const before = this.cloneCoords(target.feature);
        const after = before.slice(0, target.index + 1);

        this.commandStack.execute(
            new GeometryCommand('Trim route', target.feature, before, after, this.host.onGeometryChanged));
        this.rebuildTrimDots();
        this.removeTrimPreview();
    }

    /**
     * Paints the stretch a click would delete, so the user sees the consequence
     * before committing to it rather than discovering it through undo.
     */
    private handleTrimHover(evt: MapBrowserEvent): void {
        if (evt.dragging) {
            return;
        }
        const target = this.findTrimTarget(evt);

        //nothing to trim here: hovering a plain stretch of line, or an end vertex
        if (!target) {
            this.clearTrimPreview();
            this.setCursor('');
            return;
        }
        this.setCursor('pointer');

        //same vertex as last time — leave the existing preview alone rather than
        //rebuilding it on every mouse move
        if (this.previewedTrim
            && this.previewedTrim.feature === target.feature
            && this.previewedTrim.index === target.index) {
            return;
        }
        this.showTrimPreview(target.feature, target.index);
    }

    private showTrimPreview(feature: Feature, index: number): void {
        //from the clicked vertex to the end: that vertex survives the trim, so the
        //line starts there but only the vertices beyond it are marked for removal
        const coords = (<LineString>feature.getGeometry()).getCoordinates();
        const doomed = coords.slice(index);

        const source = this.ensureTrimPreviewLayer();
        source.clear();
        source.addFeature(new Feature({ geometry: new LineString(doomed) }));

        this.previewedTrim = { feature: feature, index: index };
        this.repaintTrimDots();
    }

    /** The dot styles read previewedTrim, so they have to be told it changed. */
    private repaintTrimDots(): void {
        this.trimDotsLayer?.changed();
    }

    /**
     * The preview layer, created on first use and then refilled — hover fires
     * constantly, and adding and removing a map layer per mouse move would churn
     * the layer stack for nothing. It carries the doomed LINE only; the vertices
     * are the trim dots themselves, recoloured (see rebuildTrimDots).
     */
    private ensureTrimPreviewLayer(): VectorSource {
        if (this.trimPreviewLayer) {
            return <VectorSource>this.trimPreviewLayer.getSource();
        }

        const source = new VectorSource();
        this.trimPreviewLayer = new VectorLayer({
            source: source,
            style: new Style({
                stroke: new Stroke({ color: 'rgba(201, 48, 44, 0.55)', width: 10 })
            }),
            zIndex: GpxLayerZIndex.trimPreview
        });
        this.trimPreviewLayer.set('id', 'gpx-trim-preview', true);
        this.map.addLayer(this.trimPreviewLayer);
        return source;
    }

    /** Hides the preview but keeps the layer for the next hover. */
    private clearTrimPreview(): void {
        this.trimPreviewLayer?.getSource()?.clear();
        this.previewedTrim = null;
        this.repaintTrimDots();
    }

    /** Drops the layer entirely (leaving trim mode, or the geometry moved). */
    private removeTrimPreview(): void {
        if (this.trimPreviewLayer) {
            this.map.removeLayer(this.trimPreviewLayer);
            this.trimPreviewLayer = null;
        }
        this.previewedTrim = null;
        this.repaintTrimDots();
    }

    private rebuildTrimDots(): void {
        this.removeTrimDots();

        const dotSource = new VectorSource();
        for (const feature of this.workingSource.getFeatures()) {
            const coords = (<LineString>feature.getGeometry()).getCoordinates();
            for (let i = 0; i < coords.length; i++) {
                const dot = new Feature({ geometry: new Point(coords[i]) });
                //remember where the dot came from so the style can tell whether a
                //hovered trim would delete it
                dot.set('trimFeature', feature, true);
                dot.set('trimIndex', i, true);
                dotSource.addFeature(dot);
            }
        }

        this.trimDotsLayer = new VectorLayer({
            source: dotSource,
            //one set of dots that recolours, rather than a second set drawn over the
            //top: the preview layer sits BELOW these, so anything it drew here would
            //simply be hidden by them
            style: (dot) => this.isDoomedByHover(<Feature>dot) ? TRIM_DOOMED_DOT_STYLE : TRIM_DOT_STYLE,
            zIndex: GpxLayerZIndex.trimDots
        });
        this.trimDotsLayer.set('id', 'gpx-trim-dots', true);
        this.map.addLayer(this.trimDotsLayer);
    }

    /**
     * Would a click on the currently hovered vertex delete this dot? The hovered
     * vertex itself survives (the trim keeps it as the new end), so only vertices
     * strictly after it are doomed.
     */
    private isDoomedByHover(dot: Feature): boolean {
        const hovered = this.previewedTrim;
        return !!hovered
            && dot.get('trimFeature') === hovered.feature
            && <number>dot.get('trimIndex') > hovered.index;
    }

    private removeTrimDots(): void {
        if (this.trimDotsLayer) {
            this.map.removeLayer(this.trimDotsLayer);
            this.trimDotsLayer = null;
        }
    }

    // -------------------------------------------------------------------------
    // waypoints — click to add/edit, drag to move (Translate)
    // -------------------------------------------------------------------------

    private startWaypoints(): void {
        const self = this;
        this.setCursor('crosshair');

        this.clickListenerKey = (evt: MapBrowserEvent) => {
            const feature = self.waypointLayer.findWaypointAtPixel(evt);
            if (feature) {
                self.host.onWaypointEditRequested(feature);
            } else {
                const lonLat = olProj.toLonLat(evt.coordinate);
                self.host.onWaypointAddRequested([lonLat[0], lonLat[1]]);
            }
        };
        this.map.on('singleclick', this.clickListenerKey);

        this.translate = new Translate({
            layers: [this.waypointLayer.olLayer]
        });
        this.translate.on('translatestart', function (evt) {
            const geometry = <Point>evt.features.item(0)?.getGeometry();
            if (geometry) {
                const lonLat = olProj.toLonLat(geometry.getCoordinates());
                self.translateSnapshot = [lonLat[0], lonLat[1]];
            }
        });
        this.translate.on('translateend', function (evt) {
            const feature = <Feature>evt.features.item(0);
            const geometry = <Point>feature?.getGeometry();
            if (feature && geometry && self.translateSnapshot) {
                const lonLat = olProj.toLonLat(geometry.getCoordinates());
                self.host.onWaypointMoved(feature, self.translateSnapshot, [lonLat[0], lonLat[1]]);
            }
            self.translateSnapshot = null;
        });
        this.map.addInteraction(this.translate);
    }

    // -------------------------------------------------------------------------
    // helpers
    // -------------------------------------------------------------------------

    /**
     * Records a finished extension as one command per appended vertex, so undo
     * peels the new vertices off one at a time (rather than removing the whole
     * extension at once). Each command sets an absolute coordinate list, so a
     * chain of them undoes/redoes correctly in sequence. Falls back to a single
     * command if the result isn't a clean append of the original line.
     */
    private pushExtendCommands(feature: Feature, before: Coordinate[], after: Coordinate[]): void {
        if (after.length <= before.length || !this.isPrefix(before, after)) {
            this.commandStack.execute(
                new GeometryCommand('Extend route', feature, before, after, this.host.onGeometryChanged),
                true);
            return;
        }

        for (let i = before.length; i < after.length; i++) {
            this.commandStack.execute(
                new GeometryCommand('Extend route', feature, after.slice(0, i), after.slice(0, i + 1), this.host.onGeometryChanged),
                true);
        }
    }

    /** True when every coordinate of `prefix` matches the start of `full`. */
    private isPrefix(prefix: Coordinate[], full: Coordinate[]): boolean {
        if (prefix.length > full.length) {
            return false;
        }
        for (let i = 0; i < prefix.length; i++) {
            if (prefix[i][0] !== full[i][0] || prefix[i][1] !== full[i][1]) {
                return false;
            }
        }
        return true;
    }

    private cloneCoords(feature: Feature): Coordinate[] {
        return (<LineString>feature.getGeometry()).getCoordinates().map(c => c.slice() as Coordinate);
    }

    private coordsEqual(a: Coordinate[], b: Coordinate[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) {
                return false;
            }
        }
        return true;
    }

    private setCursor(cursor: string): void {
        const target = this.map.getTargetElement() as HTMLElement;
        if (target) {
            target.style.cursor = cursor;
        }
    }
}
